import { useCallback, useMemo, useState } from "react";
import { catchError, of, tap } from "rxjs";

import type { APILibraryCategoryResponseType } from "@/core/data_sources/models/APILibraryCategory";
import type { APILibraryTemplateResponseType } from "@/core/data_sources/models/APILibraryTemplate";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import { DataTable, PageHeader } from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { LibraryTemplatesStore } from "../templates/data_sources/store";
import { CreateCategoryDialog } from "./components/CreateCategoryDialog";
import { EditCategoryDrawer } from "./components/EditCategoryDrawer";
import { LibraryCategoriesStore } from "./data_sources/store";

export function CategoriesPage() {
  const { state, hooks } = useStore(LibraryCategoriesStore.instance);
  const { state: templateState, hooks: templateHooks } = useStore(
    LibraryTemplatesStore.instance,
  );
  const { actions: appActions } = useStore(AppStore.instance);
  const { setQuery } = hooks.useCategoryStoreService({
    page: 1,
    page_size: 10,
  });
  templateHooks.useTemplateStoreService({
    page: 1,
    page_size: 1000,
  });
  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);
  const closeDrawer = useCallback(() => {
    appActions.rightDrawer("close");
  }, [appActions]);
  const refresh = useCallback(() => {
    setQuery((prev) => ({ ...prev }));
  }, [setQuery]);
  const handleSaved = useCallback(() => {
    refresh();
    closeDialog();
    closeDrawer();
  }, [closeDialog, closeDrawer, refresh]);
  const openCreateCategoryDialog = useCallback(() => {
    appActions.dialog(
      "open",
      <DialogTemplate>
        <CreateCategoryDialog
          onCancel={closeDialog}
          onCreatedSuccess={handleSaved}
        />
      </DialogTemplate>,
    );
  }, [appActions, closeDialog, handleSaved]);
  const openEditCategoryDrawer = useCallback(
    (category: APILibraryCategoryResponseType) => {
      appActions.rightDrawer(
        "open",
        480,
        <EditCategoryDrawer
          category={category}
          onClose={closeDrawer}
          onSaved={handleSaved}
        />,
      );
    },
    [appActions, closeDrawer, handleSaved],
  );
  const openDeleteCategoryDialog = useCallback(
    (category: APILibraryCategoryResponseType) => {
      appActions.dialog(
        "open",
        <DialogTemplate>
          <DeleteCategoryDialog
            category={category}
            onCancel={closeDialog}
            onDeleted={handleSaved}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, handleSaved],
  );
  const categories = state?.list.map((category) => category.getData()) ?? [];
  const templates =
    templateState?.list.map((template) => template.getData()) ?? [];
  const columns = useMemo(
    () => [
      {
        header: "Category",
        key: "category",
        render: (category: APILibraryCategoryResponseType) => (
          <div className="font-extrabold text-ink">{category.name}</div>
        ),
      },
      {
        header: "Used by",
        key: "usedBy",
        render: (category: APILibraryCategoryResponseType) => (
          <span className="text-sm text-ink-muted">
            {getCategoryUseCount(category.name ?? "", templates)} templates
          </span>
        ),
      },
      {
        className: "w-44",
        header: "",
        key: "actions",
        render: (category: APILibraryCategoryResponseType) => {
          const useCount = getCategoryUseCount(category.name ?? "", templates);

          return (
            <div className="flex justify-end gap-2">
              <Button
                onClick={() => openEditCategoryDrawer(category)}
                size="sm"
              >
                Edit
              </Button>
              <Button
                disabled={category.is_system || useCount > 0}
                onClick={() => openDeleteCategoryDialog(category)}
                size="sm"
                variant="danger"
              >
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    [openDeleteCategoryDialog, openEditCategoryDrawer, templates],
  );

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={openCreateCategoryDialog} variant="primary">
            Create Category
          </Button>
        }
        breadcrumb={["Library", "Categories"]}
        subtitle="Global categories used to organize templates."
        title="Categories"
      />
      <DataTable
        columns={columns}
        rowKey={(category) => String(category.id ?? category.name ?? "")}
        rows={categories}
      />
    </>
  );
}

function DeleteCategoryDialog({
  category,
  onCancel,
  onDeleted,
}: {
  readonly category: APILibraryCategoryResponseType;
  readonly onCancel: () => void;
  readonly onDeleted: () => void;
}) {
  const { actions } = useStore(LibraryCategoriesStore.instance);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = useCallback(() => {
    if (!category.id) {
      setError("Category id is required.");
      return;
    }
    setDeleting(true);
    setError("");
    actions
      .deleteCategory(String(category.id))
      .pipe(
        tap(() => {
          onDeleted();
        }),
        catchError((requestError) => {
          setError(
            requestError instanceof ErrorResponse
              ? requestError.error.message
              : requestError instanceof Error
                ? requestError.message
                : "Unable to delete category.",
          );
          return of(null);
        }),
        tap(() => setDeleting(false)),
      )
      .subscribe();
  }, [actions, category.id, onDeleted]);

  return (
    <section className="w-md max-w-full">
      <div className="border-b border-border px-6 pb-3 pt-5">
        <h2 className="text-[17px] font-semibold text-ink">Delete category</h2>
        <p className="mt-1 text-[13px] text-ink-subtle">
          This removes <strong>{category.name}</strong> from the global Library
          catalog.
        </p>
      </div>
      <div className="px-6 py-5">
        <p className="text-sm text-ink-muted">
          This action is only available when no templates use the category.
        </p>
        {error ? <p className="mt-3 text-xs text-critical">{error}</p> : null}
      </div>
      <div className="flex justify-end gap-2 border-t border-border bg-panel-muted px-6 py-3.5">
        <Button disabled={deleting} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={deleting} onClick={handleDelete} variant="danger">
          {deleting ? "Deleting..." : "Delete category"}
        </Button>
      </div>
    </section>
  );
}

function getCategoryUseCount(
  category: string,
  templates: readonly APILibraryTemplateResponseType[],
) {
  return templates.filter((template) =>
    template.categories?.some((item) => item.name === category),
  ).length;
}
